<template>
  <div class="wg-order-flow">
    <div class="wg-order-flow__back-row">
      <router-link
        :to="{ name: 'OverviewWhiteGlove', params: { siteId: $route.params.siteId } }"
        class="wg-order-flow__back-link"
      >
        <i class="material-icons">arrow_back</i>
        <span>Back to {{ backLabel }}</span>
      </router-link>
      <white-glove-admin-mode-toggle />
    </div>

    <h2 class="wg-order-flow__title mainColor--text">
      Order a content audit
    </h2>
    <p class="wg-order-flow__subtitle mainColor--text">
      We’ll analyze your entire site and create content plans to grow your traffic.
    </p>

    <div v-if="siteLoading" class="wg-order-flow__loading mainColor--text">
      Loading site…
    </div>
    <div v-else-if="siteLoadError" class="wg-order-flow__error mainColor--text">
      We couldn’t load this site. Try again or return to your projects.
    </div>
    <site-content-audit-widget
      v-else-if="site && site.id"
      :site="site"
      :key="site.id"
    />

    <async-tasks-popup
      v-if="siteId"
      :site-id="siteId"
      :campaign-id="domainLevelCampaign ? domainLevelCampaign.id : 0"
    />
  </div>
</template>

<script>
import AsyncTasksPopup from '@/components/AsyncTasksPopup.vue'
import SiteContentAuditWidget from '@/components/SitesSingleView/WhiteGlove/SiteContentAuditWidget/Index.vue'
import WhiteGloveAdminModeToggle from '@/components/SitesSingleView/WhiteGlove/WhiteGloveAdminModeToggle.vue'

export default {
  name: 'OrderContentAuditWhiteGlove',
  components: {
    AsyncTasksPopup,
    SiteContentAuditWidget,
    WhiteGloveAdminModeToggle,
  },
  data () {
    return {
      siteLoading: true,
      siteLoadError: false,
      campaigns: [],
    }
  },
  computed: {
    siteId () {
      const id = parseInt(this.$route.params.siteId, 10)
      return Number.isFinite(id) ? id : null
    },
    site () {
      if (!this.siteId) {
        return null
      }
      return this.$store.getters.siteById(this.siteId) || null
    },
    backLabel () {
      return (this.site && this.site.name) ? this.site.name : 'site'
    },
    domainLevelCampaigns () {
      return this.campaigns.filter((item) => item.isDomainLevelCampaign)
    },
    domainLevelCampaign () {
      return this.domainLevelCampaigns.length > 0 ? this.domainLevelCampaigns[0] : null
    },
  },
  watch: {
    siteId: {
      handler () {
        this.ensureSiteLoaded()
      },
    },
    // POP Authority sessions never use this page: the setup wizard lives inline on the overview
    '$store.getters.isPopAuthorityUser': {
      handler (v) {
        if (v) this.redirectPopAuthorityToOverview()
      },
      immediate: true,
    },
  },
  mounted () {
    this.ensureSiteLoaded()
  },
  methods: {
    redirectPopAuthorityToOverview () {
      const siteId = this.$route.params.siteId
      if (siteId == null || siteId === '') {
        this.$router.replace('/sites')
        return
      }
      this.$router.replace({ name: 'OverviewWhiteGlove', params: { siteId: String(siteId) } })
    },
    ensureSiteLoaded () {
      if (!this.siteId) {
        this.siteLoading = false
        this.siteLoadError = true
        return Promise.resolve()
      }
      this.siteLoading = true
      this.siteLoadError = false
      return this.$store.dispatch('loadSite', this.siteId)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS' && response.data.site) {
            this.$store.commit('setSite', response.data.site)
          } else {
            this.siteLoadError = true
          }
        })
        .catch(() => {
          this.siteLoadError = true
        })
        .finally(() => {
          this.siteLoading = false
          if (!this.siteLoadError) {
            this.loadCampaigns()
          }
        })
    },
    loadCampaigns () {
      if (!this.siteId) {
        this.campaigns = []
        return Promise.resolve()
      }
      return this.$store.dispatch('getCampaignsForSite', this.siteId)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.campaigns = response.data.campaigns || []
          }
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.wg-order-flow {
  padding: 20px 20px 0;

  &__back-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
  }

  &__title {
    font-size: 1.46rem;
    font-weight: 800;
    line-height: 1.25;
    margin: 30px 0 12px;
    letter-spacing: -0.02em;
  }

  &__subtitle {
    font-size: 0.9rem;
    line-height: 1.5;
    opacity: 0.9;
    margin: 0 0 20px;
    max-width: 40rem;
  }

  &__loading,
  &__error {
    font-size: 0.95rem;
    margin: 16px 0;
  }

  &__back-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--v-mainColor-base);
    font-size: 0.9rem;
    font-weight: 500;
    text-decoration: none;

    .material-icons {
      font-size: 18px;
    }
  }
}
</style>
